import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Mail } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { BrandButton } from "@/components/brand/brand-button";
import { BrandInput } from "@/components/brand/brand-input";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { buildLocalizedSeoLinks, getLocalizedSeoUrls } from "@/lib/seo";
import { emailSchema } from "@/lib/validation";
import { authClient } from "../lib/auth-client";

export const Route = createFileRoute("/forgot-password")({
	component: ForgotPasswordPage,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({
			matches,
			internalPath: "/forgot-password",
		});
		const title = "Reset your password - AgentMRR";
		const description =
			"Request a password reset link for your AgentMRR account.";
		return {
			meta: [
				{ title },
				{ name: "description", content: description },
				{ name: "robots", content: "noindex, follow" },
				{ property: "og:title", content: title },
				{ property: "og:description", content: description },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
			],
			links: buildLocalizedSeoLinks({
				matches,
				internalPath: "/forgot-password",
			}),
		};
	},
});

const forgotPasswordSchema = z.object({ email: emailSchema });
type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>;

function ForgotPasswordPage() {
	const [emailSent, setEmailSent] = useState<string | null>(null);

	const form = useForm<ForgotPasswordForm>({
		resolver: zodResolver(forgotPasswordSchema),
		mode: "onSubmit",
		reValidateMode: "onChange",
		defaultValues: { email: "" },
	});

	const onSubmit = async (values: ForgotPasswordForm) => {
		try {
			const { error: apiError } = await authClient.requestPasswordReset({
				email: values.email,
				redirectTo: "/reset-password",
			});
			if (apiError) {
				form.setError("root.serverError", {
					message: apiError.message || "Failed to send reset email",
				});
				return;
			}
			setEmailSent(values.email);
		} catch (err) {
			form.setError("root.serverError", {
				message:
					err instanceof Error ? err.message : "Failed to send reset email",
			});
		}
	};

	const loading = form.formState.isSubmitting;
	const serverError = form.formState.errors.root?.serverError?.message;

	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground pt-[4.5rem]">
			<PublicHeader />
			<main className="flex flex-1 flex-col items-center justify-center px-4 py-12">
				<div className="w-full max-w-[420px]">
					<div className="mb-8">
						<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
							&gt; Auth / reset
						</span>
					</div>

					<div className="border border-border bg-card p-6 sm:p-8">
						{emailSent ? (
							<>
								<div className="space-y-2">
									<h1
										className="font-mono text-2xl leading-tight tracking-[-0.02em]"
										style={{ fontWeight: 400 }}
									>
										<span className="text-brand">/</span>check-email
									</h1>
									<p className="text-base text-muted-foreground">
										Reset link sent to{" "}
										<span className="font-mono text-foreground">
											{emailSent}
										</span>
									</p>
								</div>

								<div className="mt-6 space-y-3">
									<BrandButton
										asChild
										variant="outline"
										size="lg"
										className="w-full"
									>
										<Link to="/login">
											<ArrowLeft className="size-3.5" />
											Back to login
										</Link>
									</BrandButton>
									<p className="text-center font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
										Didn't receive it?{" "}
										<button
											type="button"
											onClick={() => {
												setEmailSent(null);
												form.reset();
											}}
											className="text-brand transition-colors hover:text-brand-hover"
										>
											Try again
										</button>
									</p>
								</div>
							</>
						) : (
							<>
								<div className="space-y-2">
									<h1
										className="font-mono text-2xl leading-tight tracking-[-0.02em]"
										style={{ fontWeight: 400 }}
									>
										<span className="text-brand">/</span>reset-password
									</h1>
									<p className="text-base text-muted-foreground">
										We'll email you a reset link.
									</p>
								</div>

								{serverError && (
									<div className="mt-5 border border-destructive/30 bg-destructive/10 px-3 py-2 font-mono text-sm uppercase tracking-[0.12em] text-destructive">
										{serverError}
									</div>
								)}

								<Form {...form}>
									<form
										onSubmit={form.handleSubmit(onSubmit)}
										className="mt-6 space-y-4"
										noValidate
									>
										<FormField
											control={form.control}
											name="email"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Email</FormLabel>
													<div className="relative">
														<Mail className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
														<FormControl>
															<BrandInput
																autoFocus
																type="email"
																autoComplete="email"
																placeholder="you@domain.com"
																className="pl-9"
																{...field}
															/>
														</FormControl>
													</div>
													<FormMessage />
												</FormItem>
											)}
										/>

										<BrandButton
											type="submit"
											size="lg"
											disabled={loading}
											className="mt-1 w-full"
										>
											{loading ? "Sending" : "Send reset link"}
										</BrandButton>
									</form>
								</Form>
							</>
						)}
					</div>

					<p className="mt-6 text-center font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
						Remember your password?{" "}
						<Link
							to="/login"
							className="text-brand transition-colors hover:text-brand-hover"
						>
							Log in
						</Link>
					</p>
				</div>
			</main>
			<PublicFooter />
		</div>
	);
}
